import store from '@ps/store'
import { Event, EventBus } from '@ps/utils/event-bus'
import { addChildAt, setChildIndex } from '@ps/bridge/qc/node'
import qc from 'qc'
import * as sceneConfigScript from '@ps/bridge/qc/scripts/scene-config'
import {
  checkSceneIsSupportProp,
  defaultSceneConfig,
  GlobalLevel,
  SceneConfigScript,
  SceneType,
} from '@ps/bridge/qc/scripts/scene-config'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import {
  AstActionItem,
  AstConditionItem,
  AstEventItem,
  EditorDataScript,
} from '@ps/bridge/qc/scripts/editor'
import _ from 'lodash'
import gameIframe from '@ps/bridge/iframe/iframe'
import { find, findByUuid } from '@ps/bridge/qc/node/utils'
import animateScrollTo from 'animated-scroll-to'
import {
  ISceneThumbnail,
  IUiLayout,
} from '@ps/store/workbench/scene/index.type'
import nodePool from '@ps/bridge/qc/node-pool'
import { isIvPage, workbenchRoute } from '@ps/router/config'
import { closePreviewAllBox2dShape } from './box2d'
import { getCarouselNodeInScene } from '@ps/bridge/qc/base'
import {
  getIvEndingInstallNode,
  getSceneHotSpotGroup,
} from '@ps/store/interactive-video/brain/brain'
import {
  getIvCurSceneVideoNode,
  pausePreviewTimeline,
} from '@ps/store/interactive-video/timeline/timeline'

export type SwitchSceneActionValue = {
  newValue: {
    uuid: string
  }
  oldValue: {
    uuid: string
  }
}
export const getSwitchSceneAction = (value: SwitchSceneActionValue) => {
  /**
   * 切换场景
   * @param uuid 需要切换场景的 uuid
   * @param linkageUpdate 是否联动更新所有切换逻辑的逻辑，如更新 currentScene 数据，隐藏普通场景等，如果是内部触发的切换场景，不需要更新这些联动信息
   */
  const switchScene = async (uuid: string, linkageUpdate = true) => {
    const root = gameIframe.UIRoot
    const node = find(root, (node) => node.uuid === uuid)
    if (!node) {
      console.log('没有找到该场景')
      return
    }
    if (linkageUpdate) {
      store.commit('workspace/sceneManager/updateCurrentScene', node)
    }

    const gamePlay = gameIframe.gamePlay
    let normalScene = gamePlay.children
    const isNormMaterial = store.state.workspace.project.isNormMaterial
    if (checkSceneIsSupportProp('global') && isNormMaterial) {
      normalScene = gamePlay.children.filter((scene) => {
        return (
          (scene.SceneConfig &&
            scene.SceneConfig.sceneType === SceneType.NORMAL) ||
          (scene.SceneConfig &&
            typeof scene.SceneConfig.sceneType === 'undefined')
        )
      })
    }
    const globalScene = gamePlay.children.find((item) => {
      const sceneConfig = item.SceneConfig
      return sceneConfig && sceneConfig.sceneType === SceneType.GLOBAL
    })
    const playScene = gamePlay.children.filter((item) => {
      const sceneConfig = item.SceneConfig
      return sceneConfig && sceneConfig.sceneType === SceneType.PLAY
    })
    const hideScene = (node?: qc.Node) => {
      if (node && node.visible) {
        node.visible = false
      }
    }
    const toggleGlobalSceneLevel = (level: GlobalLevel) => {
      const maxLevel = gamePlay.children.length - 1
      if (!globalScene) return
      if (level) {
        globalScene.switchParent(gamePlay, maxLevel)
      } else {
        // 全局场景即使在底部，也是在玩法场景之上
        globalScene.switchParent(gamePlay, playScene.length)
      }
    }
    const showScene = (node?: qc.Node) => {
      if (node && node.uuid) {
        // 内部中切换普通场景联动的切换全局场景和玩法场景，不需要更新联动关系
        switchScene(node.uuid, false)
      }
    }
    const SceneConfig = sceneConfigScript.getScript(node)!
    const normalSceneExecution = () => {
      normalScene.forEach((item) => {
        if (item.uuid === uuid) {
          item.visible = true
          // 互动视频项目不支持全局场景
          if (SceneConfig.isShowGlobal && !isIvPage()) {
            // 如果需要显示全局场景，但是全局场景当前是隐藏状态，则显示
            showScene(globalScene)
            // 调整全局场景的层级
            toggleGlobalSceneLevel(SceneConfig.globalLevel!)
          } else {
            hideScene(globalScene)
          }
          if (SceneConfig.playUUID) {
            playScene &&
              playScene.length &&
              playScene.forEach((node) => {
                if (node.uuid === SceneConfig.playUUID) {
                  showScene(node)
                } else {
                  hideScene(node)
                }
              })
          } else {
            playScene.forEach((unit) => {
              hideScene(unit)
            })
          }
        } else {
          hideScene(item)
        }
      })
      if (isIvPage()) {
        let resNode
        if (SceneConfig.isEnding) {
          // 互动视频切换到结束场景时激活跳转商店热区
          resNode = getIvEndingInstallNode(node)
        } else {
          // 互动视频项目切换场景时激活到场景的视频节点
          resNode = getIvCurSceneVideoNode()
          // 切换场景时隐藏掉热区组内的所有热区节点，由时间轴预览逻辑统一控制元素显隐
          const group = getSceneHotSpotGroup(node)
          if (group) {
            group.children.forEach((child) => {
              child.visible = false
            })
          }
        }
        if (resNode) {
          store.dispatch('workspace/game/updateActiveNode', {
            node: resNode,
            record: false,
          })
          store.commit('workspace/rightPane/switchToNodeConfigTab', {
            root: true,
          })
        } else {
          store.dispatch('workspace/game/clearActiveNode', {
            record: false,
          })
          store.dispatch('workspace/rightPane/clearTab', {
            root: true,
          })
        }
      } else {
        store.dispatch('workspace/rightPane/switchToStageConfigTab', {
          root: true,
        })
        if (store.state.workspace.game.activeNode) {
          store.commit('workspace/rightPane/switchToNodeConfigTab', {
            root: true,
          })
        }
      }
    }
    const hideAllOtherScene = () => {
      if (linkageUpdate) {
        // 如果是外部切换场景，需要隐藏所有普通场景和全局场景
        gamePlay.children.forEach((item) => (item.visible = item === node))
      }
    }
    const globalSceneExecution = () => {
      node.visible = true
      hideAllOtherScene()
      store.dispatch('workspace/rightPane/switchToGlobalConfigTab', {
        root: true,
      })
    }
    const playSceneExecution = () => {
      node.visible = true
      hideAllOtherScene()
    }
    // 不同场景执行不同事件
    switch (SceneConfig.sceneType) {
      case SceneType.GLOBAL:
        globalSceneExecution()
        break
      case SceneType.PLAY:
        playSceneExecution()
        break
      case SceneType.NORMAL:
        normalSceneExecution()
        break
      default:
        normalSceneExecution()
        break
    }
    if (typeof SceneConfig.sceneType !== 'undefined') {
      // 更新场景编辑状态
      store.commit(
        'workspace/sceneManager/updateSceneEditType',
        SceneConfig.sceneType,
      )
    }
    // VP持久化记录事件表
    await store.dispatch('workspace/game/visualProgram/initAST')
    // 更新画线元件信息
    await store.dispatch('workspace/game/drawLine/initData')
    // 切换场景时关闭刚体轮廓
    store.commit('workspace/game/updatePreviewBox2dShapeState', {
      val: false,
    })
    store.commit('workspace/game/updatePreviewBox2dShapeAllState', {
      val: false,
    })
    closePreviewAllBox2dShape()
    if (isIvPage()) {
      pausePreviewTimeline()
    }
  }

  return {
    execute: async ({ newValue }: SwitchSceneActionValue) => {
      await switchScene(newValue.uuid)
      if (isIvPage()) {
        // 互动视频进入场景后记录是否有编辑操作
        const node = nodePool.find(newValue.uuid)
        if (node) {
          node.hasEditThisTime = false
        }
      }
    },
    undo: async ({ oldValue }: SwitchSceneActionValue) => {
      await switchScene(oldValue.uuid)
    },
    value,
    methodName: 'getSwitchSceneAction',
  }
}
export type eventsToNodeItem<T> = {
  astIndex: number
  index: number
  value: T
  sceneId: string
}

export type RemoveSceneActionValue = {
  newValue: {
    node: qc.Node
    activeSceneUuid: string
    activeUuid: string
  }
  oldValue: {
    node: qc.Node
    parent: qc.Node
    index: number
    activeSceneUuid: string
    activeUuid: string
    activeUuids: string[]
    thumbnail: ISceneThumbnail
    nodesLayout: IUiLayout
    eventsOnNode: AstEventItem[]
    eventsToNode: {
      condition: eventsToNodeItem<AstConditionItem>[]
      action: eventsToNodeItem<AstActionItem>[]
    }
  }
}
export const getRemoveSceneAction = (value: RemoveSceneActionValue[]) => {
  return {
    execute: async (value: RemoveSceneActionValue[]) => {
      // 为啥要在这里锁定操作？因为 deleteVPComponent 中会请求接口，需要锁定用户操作
      store.commit('workspace/lockPage')
      for (const task of value) {
        const { newValue } = task
        const { node, activeSceneUuid, activeUuid } = newValue

        // 移除场景节点前，需要更新场景视频关联数据
        await store.dispatch('workspace/game/video/setVideosManager')
        // 移除场景之后，deleteVPComponent 之前更新场景列表，保证UI效果更新不需要等到 deleteVPComponent 结果
        await store.dispatch('workspace/game/removeNode', {
          nodes: [node],
          record: false,
        })
        const sceneList = store.state.workspace.sceneManager.sceneList
        const currentSceneNode =
          store.state.workspace.sceneManager.currentSceneNode
        store.commit(
          'workspace/sceneManager/updateSceneList',
          sceneList.filter((item: qc.Node) => item.uuid !== node.uuid),
        )
        await Promise.all([
          store.dispatch('workspace/game/visualProgram/deleteVPComponent', {
            qcNode: node,
            isDeleteResource: false,
          }),
        ])
        // 如果删除的场景节点就是当前节点，删除的时候需要把当前节点设置为 null
        if (node === currentSceneNode) {
          store.commit('workspace/sceneManager/updateCurrentScene', null)
        }

        // 删除必要数据才完切换场景
        if (activeSceneUuid) {
          await store.dispatch('workspace/sceneManager/switchScene', {
            uuid: activeSceneUuid,
            record: false,
          })
        } else {
          // 如果不需要切换场景，则需要清空当前的虚拟节点
          await store.dispatch('workspace/stage/destroyAllShape')
        }

        // 如果有激活节点，则重置为空
        if (activeUuid) {
          await store.dispatch('workspace/game/clearActiveNode', {
            record: false,
          })
        }

        // 重新实例化故事板
        await store.dispatch('workspace/storyboard/removeScene', {
          sceneId: node.uuid,
        })
      }
      EventBus.$emit(Event.SILENT_SAVE)
      // 之前一直是用websocket去掉loading，有时没有收到websocket会一直loading，导致用户操作不了，这里做个兜底处理
      store.commit('workspace/unlockPage')
    },
    undo: async (value: RemoveSceneActionValue[]) => {
      for (const task of value) {
        const { oldValue } = task
        const {
          node,
          activeSceneUuid,
          parent,
          index,
          activeUuid,
          activeUuids,
          nodesLayout,
          eventsOnNode,
          eventsToNode,
        } = oldValue
        // 指向该场景的事件
        store.dispatch('workspace/game/visualProgram/recoverNode', {
          eventsToNode,
        })
        // 存在该场景的事件
        store.dispatch('workspace/game/visualProgram/saveVpAst', {
          node,
          ast: eventsOnNode,
        })
        store.commit(
          'workspace/sceneManager/addSceneUIPositionItem',
          nodesLayout,
        )
        addChildAt({
          node,
          parent,
          index,
        })
        await store.dispatch('workspace/sceneManager/switchScene', {
          uuid: activeSceneUuid,
          record: false,
        })
        await store.dispatch('workspace/storyboard/addScene', {
          qcNode: node,
        })
        // 新增场景节点前，需要更新场景视频关联数据
        await store.dispatch('workspace/game/video/setVideosManager')
        store.commit('workspace/sceneManager/updateSceneList')
        store.commit('workspace/game/selectNode', {
          uuid: activeUuid,
          uuids: activeUuids,
        })
        setTimeout(() => {
          store.dispatch('workspace/storyboard/renderEdges')
        }, 100)
      }
    },
    value,
    methodName: 'getRemoveSceneAction',
  }
}
export type RemovePlaySceneActionValue = {
  newValue: {
    nodes: qc.Node[]
    activeSceneUuid: string
    activeUuid: string
    newNode: qc.Node
  }
  oldValue: {
    nodes: qc.Node[]
    newNode: qc.Node
    parent: qc.Node
    nodeIndex: Record<string, number>
    activeSceneUuid: string
    activeUuid: string
    activeUuids: string[]
    nodesLayout: Record<string, IUiLayout>
    eventsOnNode: Record<string, AstEventItem[]>
    eventsToNode: Record<
      string,
      {
        condition: eventsToNodeItem<AstConditionItem>[]
        action: eventsToNodeItem<AstActionItem>[]
      }
    >
  }
}

export const getRemovePlaySceneAction = (value: RemovePlaySceneActionValue) => {
  const normalSceneList: qc.Node[] =
    store.getters['workspace/game/sceneManager/normalSceneList']
  const playSceneList =
    store.getters['workspace/game/sceneManager/playSceneList']
  const sceneEditType = store.state.workspace.sceneManager.sceneEditType
  return {
    execute: async ({ newValue }: RemovePlaySceneActionValue) => {
      const { nodes, newNode, activeSceneUuid, activeUuid } = newValue
      nodes.forEach(async (item) => {
        await store.dispatch('workspace/sceneManager/removeScene', {
          uuid: item.uuid,
          record: false,
          switchUUID: '',
          force: true,
        })
      })
      if (newNode) {
        await store.dispatch('workspace/sceneManager/addScene', {
          node: newNode,
          isSwitchScene: sceneEditType !== SceneType.PLAY,
          record: false,
        })
      }
      const currentSceneNode =
        store.state.workspace.sceneManager.currentSceneNode
      const isRemoveCurrent = nodes.some(
        (item) => item.uuid === currentSceneNode.uuid,
      )
      // 当前场景被移除，切换到其他场景
      if (isRemoveCurrent) {
        let uuid = ''
        if (newNode) {
          uuid = newNode.uuid
        } else if (activeSceneUuid) {
          uuid = activeSceneUuid
        } else {
          const removeList = nodes.map((el) => el.uuid)
          const lastNormalScene = normalSceneList.filter(
            (temp) => removeList.indexOf(temp.uuid) === -1,
          )
          uuid =
            lastNormalScene.length && lastNormalScene[0]
              ? lastNormalScene[0].uuid
              : ''
        }
        if (uuid) {
          // 如果是在普通场景视图上面的话，删除场景之后，如果没有选中场景节点，需要选中第一个场景
          await store.dispatch('workspace/sceneManager/switchScene', {
            uuid,
            record: false,
          })
        }
      }
      // 删除玩法场景后无玩法场景，如果当前编辑类型是玩法编辑，自动从玩法编辑跳出
      if (playSceneList.length < 1 && sceneEditType === SceneType.PLAY) {
        store.dispatch('workspace/sceneManager/switchBackNormalScene', '', {
          root: true,
        })
      }

      // EventBus.$emit(Event.SILENT_SAVE)
    },
    undo: async ({ oldValue }: RemovePlaySceneActionValue) => {
      const {
        nodes,
        newNode,
        activeSceneUuid,
        activeUuid,
        parent,
        nodeIndex,
        activeUuids,
        nodesLayout,
        eventsOnNode,
        eventsToNode,
      } = oldValue

      if (newNode) {
        await store.dispatch('workspace/sceneManager/removeScene', {
          uuid: newNode.uuid,
          record: false,
          switchUUID: '',
          force: true,
        })
      }

      nodes.forEach(async (node) => {
        // 指向该场景的事件
        store.dispatch('workspace/game/visualProgram/recoverNode', {
          eventsToNode: eventsToNode[node.uuid],
        })
        // 存在该场景的事件
        store.dispatch('workspace/game/visualProgram/saveVpAst', {
          node,
          ast: eventsOnNode[node.uuid],
        })
        store.commit(
          'workspace/sceneManager/addSceneUIPositionItem',
          nodesLayout[node.uuid],
        )
        console.log(node.uniqueName)
        console.log(nodeIndex[node.uuid])
        addChildAt({
          node,
          parent,
          index: nodeIndex[node.uuid],
        })
        // await store.dispatch('workspace/storyboard/addScene', {
        //   qcNode: node,
        // })
        // 新增场景节点前，需要更新场景视频关联数据
        await store.dispatch('workspace/game/video/setVideosManager')
        store.commit('workspace/sceneManager/updateSceneList')
      })

      await store.dispatch('workspace/sceneManager/switchScene', {
        uuid: activeSceneUuid,
        record: false,
      })
      store.dispatch(
        'workspace/game/updateActiveNode',
        {
          node: nodePool.find(activeUuid),
          record: false,
        },
        {
          root: true,
        },
      )
      // store.commit('workspace/game/selectNode', {
      //   uuid: activeUuid,
      //   uuids: activeUuids,
      // })
    },
    value,
    methodName: 'getRemovePlaySceneAction',
  }
}

export type AddSceneActionValue = {
  newValue: {
    node: qc.Node
    index: number
    isSwitchScene: boolean // 是否需要切换场景
    sceneType: SceneType // 场景类型
    isBatch: boolean // 是否批量新增场景
  }
  oldValue: {
    node: qc.Node
    sceneUuid: string // 回滚后需要更新的当前场景的 id
  }
}
export const getAddSceneAction = (value: AddSceneActionValue) => {
  return {
    execute: async ({ newValue }: AddSceneActionValue) => {
      const { node, index, isSwitchScene, sceneType, isBatch } = newValue
      if (!isBatch) {
        store.commit('workspace/lockPage')
      }

      // 新增节点的时候在 recordHistory 之前已经增加节点了，所以需要判断节点树上是否有该节点
      if (!findByUuid(node.uuid)) {
        addChildAt({
          node,
          parent: gameIframe.gamePlay,
          index,
        })
      }

      // 修改场景节点位置
      if (_.isNumber(index)) {
        gameIframe.gamePlay.setChildIndex(node, index)
      }

      // state.currentSceneNode = node
      const editorData = editorScript.getScript(node) as EditorDataScript
      editorData.$data.nodeType = 'scene'

      const sceneConfig = sceneConfigScript.getScript(node, false)

      // 复制的场景会含有脚本数据，不需要额外更新
      if (!sceneConfig) {
        //  设置 SceneConfig 的值
        await store.dispatch('workspace/sceneManager/updateSceneConfigScript', {
          uuid: node.uuid,
          config: {
            isEnding: defaultSceneConfig.isEnding,
          },
          record: false,
        })
        // 如果脚本支持 global 属性，默认需要添加是否添加全局场景和层级关系
        const isNormMaterial = store.state.workspace.project.isNormMaterial
        if (checkSceneIsSupportProp('global') && isNormMaterial) {
          await store.dispatch(
            'workspace/sceneManager/updateSceneConfigScript',
            {
              uuid: node.uuid,
              config: {
                sceneType,
                isShowGlobal:
                  sceneType === SceneType.PLAY
                    ? false
                    : defaultSceneConfig.isShowGlobal,
                globalLevel: defaultSceneConfig.globalLevel,
              },
              record: false,
            },
          )
        }
      }

      store.commit('workspace/sceneManager/updateSceneList')
      // 如果是编辑粒子或者编辑龙骨的场景，不需要新增 vpComponent，不然会有太多冗余数据
      if (
        ![
          workbenchRoute.assetTypes.particle,
          workbenchRoute.assetTypes.dragonBone,
        ].includes(store.state.workspace.assetType)
      ) {
        await store.dispatch('workspace/game/visualProgram/ensureVPComponent', {
          qcNode: node,
        })
      } else {
        console.log('编辑粒子模式不需要添加 vpComponent')
      }

      isSwitchScene &&
        (await store.dispatch('workspace/sceneManager/switchScene', {
          uuid: node.uuid,
          record: false,
        }))
      // 新增场景节点，需要更新场景视频关联数据
      await store.dispatch('workspace/game/video/setVideosManager')
      const sceneEle = document.querySelector(`#node-${node.uuid}`)
      sceneEle && animateScrollTo(sceneEle)

      EventBus.$emit(Event.ADD_GAME_OBJECT, node.uuid)
      if (!isBatch) {
        EventBus.$emit(Event.SILENT_SAVE)
        store.commit('workspace/unlockPage')
      }
      // 场景初始化，初始化轮播组件
      setTimeout(() => {
        const carouselNode = getCarouselNodeInScene(
          store.state.workspace.sceneManager.currentSceneNode,
        )
        if (carouselNode) {
          store.dispatch('workspace/game/updateCarouselGroup')
          store.dispatch('workspace/game/resetCarousel')
        }
      }, 0)
    },
    undo: async ({ oldValue }: AddSceneActionValue) => {
      store.commit('workspace/lockPage')
      // undo 的时候调用 removeScene action 里就有移除故事板节点的操作了，所以这里不需要调用故事板的 workspace/storyboard/removeScene
      const { node, sceneUuid } = oldValue
      await store.dispatch('workspace/sceneManager/removeScene', {
        uuid: node.uuid,
        record: false,
      })
      if (sceneUuid) {
        const scene = findByUuid(sceneUuid)
        scene &&
          store.dispatch(
            'workspace/sceneManager/switchScene',
            { uuid: scene.uuid, record: false },
            { root: true },
          )
        // 新增场景节点，需要更新场景视频关联数据
        await store.dispatch('workspace/game/video/setVideosManager')
      }
      store.commit('workspace/unlockPage')
    },
    value,
    methodName: 'getAddSceneAction',
  }
}

export type UpdateSceneConfigScriptActionValue = {
  newValue: {
    node: qc.Node
    config: SceneConfigScript
  }
  oldValue: {
    node: qc.Node
    config: SceneConfigScript
  }
}
export const getUpdateSceneConfigScriptAction = (
  value: UpdateSceneConfigScriptActionValue,
) => {
  return {
    execute: ({ newValue }: UpdateSceneConfigScriptActionValue) => {
      const { node, config } = newValue
      const sceneConfig = sceneConfigScript.getScript(node)
      if (sceneConfig) {
        Object.assign(sceneConfig, config)
      }
    },
    undo: ({ oldValue }: UpdateSceneConfigScriptActionValue) => {
      const { node, config } = oldValue
      const sceneConfig = sceneConfigScript.getScript(node)
      if (sceneConfig) {
        Object.assign(sceneConfig, config)
      }
    },
    value,
    methodName: 'getUpdateSceneConfigScriptAction',
  }
}

export type SetSceneIndexValue = {
  newValue: {
    node: qc.Node
    index: number
  }
  oldValue: {
    node: qc.Node
    index: number
  }
}
export const getSetSceneIndex = (value: SetSceneIndexValue) => {
  return {
    execute: ({ newValue }: SetSceneIndexValue) => {
      const { node, index } = newValue
      setChildIndex({ node, index })
      store.commit('workspace/sceneManager/updateSceneList')
    },
    undo: ({ oldValue }: SetSceneIndexValue) => {
      const { node, index } = oldValue
      setChildIndex({ node, index })
      store.commit('workspace/sceneManager/updateSceneList')
    },
    value,
    methodName: 'getSetSceneIndexAction',
  }
}
